// DbOrderBar/index.tsx
import React, { useMemo, useCallback } from 'react';
import { View, Text, TouchableOpacity, ScrollView } from 'react-native';
import DbIcon from '../DbIcon';
import { createStyles, ORDER_BAR_DEFAULTS } from './DbOrderBar.styles';
import type { DbOrderBarProps, OrderBarItem } from './DbOrderBar.types';

 const DbOrderBar: React.FC<DbOrderBarProps> = ({
  data,
  layout = 'fixed',
  title,
  titleStyle,
  showTitle = true,
  rightText,
  rightIcon,
  rightIconName,
  rightIconFamily = 'Ionicons',
  onRightPress,
  style,
  headerStyle,
  itemStyle,
  itemTextStyle,
  gap = ORDER_BAR_DEFAULTS.gap,
  padding = ORDER_BAR_DEFAULTS.padding,
  showDivider = true,
  backgroundColor,
  onItemPress,
}) => {
  const styles = useMemo(() => createStyles('light'), []);

  // 处理项目点击
  const handleItemPress = useCallback((item: OrderBarItem) => {
    if (item.disabled) return;
    item.onPress?.();
    onItemPress?.(item);
  }, [onItemPress]);

  // 渲染图标
  const renderIcon = useCallback((item: OrderBarItem) => {
    const iconSize = item.iconSize || ORDER_BAR_DEFAULTS.iconSize;
    
    if (item.icon) {
      return item.icon;
    }

    if (item.iconName) {
      return (
        <DbIcon
          name={item.iconName}
          family={item.iconFamily || 'Ionicons'}
          size={iconSize}
          color={item.iconColor || '#666666'}
        />
      );
    }

    return null;
  }, []);

  // 渲染徽标
  const renderBadge = useCallback((item: OrderBarItem) => {
    if (!item.badge) return null;

    const badgeText = typeof item.badge === 'number' && item.badge > 99 
      ? '99+' 
      : String(item.badge);

    return (
      <View style={[
        styles.badge,
        item.badgeColor && { backgroundColor: item.badgeColor }
      ]}>
        <Text style={styles.badgeText}>{badgeText}</Text>
      </View>
    );
  }, [styles]);

  // 渲染单个订单项
  const renderItem = useCallback((item: OrderBarItem, index: number) => {
    const isFixed = layout === 'fixed';
    
    return (
      <TouchableOpacity
        key={item.id || index}
        style={[
          styles.orderItem,
          isFixed && styles.orderItemFixed,
          item.disabled && styles.orderItemDisabled,
          itemStyle,
        ]}
        onPress={() => handleItemPress(item)}
        activeOpacity={0.7}
        disabled={item.disabled}
      >
        <View style={styles.iconContainer}>
          {renderIcon(item)}
          {renderBadge(item)}
        </View>
        <Text
          style={[
            styles.itemText,
            item.disabled && styles.itemTextDisabled,
            itemTextStyle,
          ]}
          numberOfLines={1}
        >
          {item.title}
        </Text>
      </TouchableOpacity>
    );
  }, [
    layout,
    itemStyle,
    itemTextStyle,
    styles,
    handleItemPress,
    renderIcon,
    renderBadge,
  ]);

  // 渲染标题栏
  const renderHeader = useCallback(() => {
    if (!showTitle && !title && !rightText && !rightIcon && !rightIconName) {
      return null;
    }

    return (
      <View style={[styles.header, headerStyle]}>
        <View style={styles.headerLeft}>
          {title && (
            <Text style={[styles.title, titleStyle]}>{title}</Text>
          )}
        </View>
        {(rightText || rightIcon || rightIconName) && (
          <TouchableOpacity
            style={styles.headerRight}
            onPress={onRightPress}
            activeOpacity={0.7}
          >
            {rightText && (
              <Text style={styles.rightText}>{rightText}</Text>
            )}
            {rightIcon || (rightIconName && (
              <DbIcon
                name={rightIconName}
                family={rightIconFamily}
                size={16}
                color="#999999"
              />
            ))}
          </TouchableOpacity>
        )}
      </View>
    );
  }, [
    showTitle,
    title,
    titleStyle,
    headerStyle,
    rightText,
    rightIcon,
    rightIconName,
    rightIconFamily,
    onRightPress,
    styles,
  ]);

  // 渲染内容
  const renderContent = useCallback(() => {
    if (layout === 'scroll') {
      return (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.scrollContainer}
          contentContainerStyle={[
            styles.scrollContent,
            { gap, paddingHorizontal: padding },
          ]}
        >
          {data.map((item, index) => renderItem(item, index))}
        </ScrollView>
      );
    }

    return (
      <View style={styles.fixedContainer}>
        {data.map((item, index) => renderItem(item, index))}
      </View>
    );
  }, [layout, data, gap, padding, styles, renderItem]);

  return (
    <View style={[
      styles.container,
      backgroundColor && { backgroundColor },
      style,
    ]}>
      {renderHeader()}
      {showDivider && <View style={styles.divider} />}
      {renderContent()}
    </View>
  );
};

export default DbOrderBar;
export type { DbOrderBarProps, OrderBarItem };
